import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { LocalHospital } from '@mui/icons-material';
import { authService } from '../services/api';

const REDIRECT_MESSAGES = {
  session_expired: 'Your session has expired. Please log in again.',
  unauthorized: 'You have been signed out. Please log in again.',
};

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectReason = new URLSearchParams(location.search).get('reason');

  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setErrorCode('');

    try {
      const response = await authService.login(formData);
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      navigate('/dashboard');
    } catch (err) {
      setErrorCode(err.response?.data?.code || '');
      setError(err.response?.data?.message || 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <Container maxWidth="sm">
        <Paper
          elevation={10}
          sx={{
            p: 5,
            borderRadius: 4,
            background: 'white',
          }}
          className="fade-in"
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <LocalHospital
              sx={{
                fontSize: 70,
                color: '#2196f3',
                mb: 2,
              }}
            />
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{ fontWeight: 700, color: '#1976d2', whiteSpace: 'nowrap' }}
            >
              Dr. Dhawal Sawant
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Login to your account
            </Typography>
          </Box>

          {redirectReason && REDIRECT_MESSAGES[redirectReason] && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {REDIRECT_MESSAGES[redirectReason]}
            </Alert>
          )}

          {error && (
            <Alert
              severity={errorCode === 'LICENSE_EXPIRED' ? 'warning' : 'error'}
              sx={{ mb: 3 }}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              margin="normal"
              required
              autoFocus
              variant="outlined"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              required
              variant="outlined"
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
            </Button>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Link
                href="#"
                variant="body2"
                sx={{
                  color: '#2196f3',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Forgot Password?
              </Link>
            </Box>
          </form>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button
              variant="text"
              onClick={() => navigate('/')}
              sx={{ color: '#666' }}
            >
              Back to Home
            </Button>
          </Box>
        </Paper>
      </Container>
    </div>
  );
};

export default LoginPage;
